import { useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';
import { TotadminPermission, useGetCreditQuery } from 'data/__generated__';

import { CopyValue } from 'components/CopyValue';
import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageActionsMenu, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import { CreditDetails as CreditInfo } from 'pages/Payments/components/PaymentInstrumentDetails';
import { PaymentsSection } from 'pages/Payments/components/PaymentsSection';

const ACTION_GROUPS: PageActionGroup[] = [
    {
        label: 'Navigation',
        items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: PAGES.credits.url }],
    },
];

export const CreditDetails = () => {
    const { creditId = '' } = useParams<{ creditId: string }>();
    // `credit` lève quand le document n'existe plus : la page le dit, et garde les paiements faits avec cet identifiant.
    const { data, loading, error } = useGetCreditQuery({ variables: { creditId } });

    if (loading && !data) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.creditDetails} />
                </HeaderTitle>
                <PageActionsMenu groups={ACTION_GROUPS} />
            </Header>
            <Content>
                <SectionContainer title="Informations" keepOpen>
                    <ColumnsSectionContainer numberOfColumns={3}>
                        <CopyValue label="Identifiant" value={creditId} />
                        {data?.credit ? (
                            <CreditInfo credit={data.credit} />
                        ) : (
                            <Message>
                                {error ? `Ce crédit n'a pas pu être chargé : ${error.message}` : 'Crédit introuvable'}
                            </Message>
                        )}
                    </ColumnsSectionContainer>
                </SectionContainer>
                <SectionContainer
                    title="Paiements"
                    isInitiallyOpen
                    requiredPermissions={[TotadminPermission.TransactionsRead]}
                >
                    <SectionColumn>
                        <PaymentsSection
                            scope={{ paymentMethodIds: [creditId] }}
                            hiddenColumnIds={['userFullName']}
                            emptyMessage="Aucun paiement avec ce crédit"
                            dataTest="credit-payments"
                        />
                    </SectionColumn>
                </SectionContainer>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    padding: 15px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Message = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
`;
